// WP-1.10 (S-18 `/recurring`, M13 RC-1..4): template rows (schedule phrase, amount or
// "variable", behavior chip, next run, pause toggle) + upcoming-window preview panel +
// schedule-builder editor.
import { CURRENCIES, parseAmountInput, type CurrencyCode, type RecurFrequency } from '@pfos/engine';
import type { RecurringTemplateCreateRequest, RecurringTemplateResponse } from '@pfos/shared';
import { Plus, Repeat } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AccountPicker } from '../components/AccountPicker';
import { Amount } from '../components/Amount';
import { EmptyState } from '../components/EmptyState';
import {
  AmountField,
  Field,
  ghostButtonInlineClass,
  inputClass,
  primaryButtonInlineClass,
  selectClass,
} from '../components/FormKit';
import { FormSheet } from '../components/FormSheet';
import { useAccounts } from '../lib/accounts';
import { ApiError } from '../lib/api';
import {
  useCreateRecurringTemplate,
  usePauseRecurringTemplate,
  usePostRecurringOccurrence,
  useRecurringTemplates,
  useResumeRecurringTemplate,
  useSkipRecurringOccurrence,
  useUpcomingOccurrences,
} from '../lib/recurring';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function describeSchedule(schedule: RecurringTemplateResponse['schedule']): string {
  const freqLabel = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', yearly: 'Yearly' }[
    schedule.frequency
  ];
  const everySuffix = schedule.everyN > 1 ? ` (every ${String(schedule.everyN)})` : '';
  if (schedule.frequency === 'daily') return `${freqLabel}${everySuffix}`;
  if (schedule.frequency === 'weekly') {
    const wd = schedule.weekday !== undefined ? (WEEKDAYS[schedule.weekday] ?? '') : '';
    return `${freqLabel} · ${wd}${everySuffix}`;
  }
  const rule = schedule.monthlyRule;
  let ruleLabel = '';
  if (rule?.kind === 'day') ruleLabel = `day ${String(rule.day)}`;
  else if (rule?.kind === 'lastDay') ruleLabel = 'last day';
  else if (rule?.kind === 'firstWeekday') ruleLabel = `first ${WEEKDAYS[rule.weekday] ?? ''}`;
  return `${freqLabel} · ${ruleLabel}${everySuffix}`;
}

function templateCurrency(
  template: RecurringTemplateResponse,
  accounts: { id: string; currency: CurrencyCode }[],
): CurrencyCode {
  if (template.base.kind === 'payable') return template.base.currency;
  const accountId =
    template.base.kind === 'transfer' ? template.base.fromAccountId : template.base.accountId;
  return accounts.find((a) => a.id === accountId)?.currency ?? 'PKR';
}

function TemplateRow({
  template,
  accounts,
}: {
  template: RecurringTemplateResponse;
  accounts: { id: string; currency: CurrencyCode }[];
}): React.JSX.Element {
  const pauseMutation = usePauseRecurringTemplate(template.id);
  const resumeMutation = useResumeRecurringTemplate(template.id);
  const skipMutation = useSkipRecurringOccurrence(template.id);
  const postMutation = usePostRecurringOccurrence(template.id);
  const [postingVariable, setPostingVariable] = useState(false);
  const [postAmount, setPostAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isVariable = template.base.amountMinor === undefined;
  const currency = templateCurrency(template, accounts);

  return (
    <li className="border-b border-border py-3 last:border-0">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-ink">{template.name}</p>
          <p className="text-xs text-muted">{describeSchedule(template.schedule)}</p>
        </div>
        <div className="text-right">
          {isVariable ? (
            <span className="text-sm text-muted">variable</span>
          ) : (
            <Amount
              currency={currency}
              minorUnits={template.base.amountMinor ?? 0}
              className="font-medium"
            />
          )}
          <p className="text-xs text-muted">
            {template.behavior === 'auto_post' ? 'auto-post' : 'confirm'}
          </p>
        </div>
      </div>
      <p className="mt-1 text-xs text-muted">Next: {template.nextRunAt.slice(0, 10)}</p>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {template.status === 'active' ? (
          <button
            type="button"
            className={ghostButtonInlineClass}
            disabled={pauseMutation.isPending}
            onClick={() => {
              pauseMutation.mutate();
            }}
          >
            Pause
          </button>
        ) : (
          <button
            type="button"
            className={ghostButtonInlineClass}
            disabled={resumeMutation.isPending}
            onClick={() => {
              resumeMutation.mutate();
            }}
          >
            Resume
          </button>
        )}
        {template.status === 'active' && (
          <>
            <button
              type="button"
              className={ghostButtonInlineClass}
              disabled={skipMutation.isPending}
              onClick={() => {
                skipMutation.mutate();
              }}
            >
              Skip
            </button>
            {isVariable && postingVariable ? (
              <div className="flex items-center gap-2">
                <AmountField
                  id={`post-amount-${template.id}`}
                  ariaLabel="Post amount"
                  currency={currency}
                  value={postAmount}
                  onChange={setPostAmount}
                />
                <button
                  type="button"
                  className={primaryButtonInlineClass}
                  disabled={postMutation.isPending}
                  onClick={() => {
                    setError(null);
                    const parsed = parseAmountInput(postAmount, { defaultCurrency: currency });
                    if (!parsed.ok) return;
                    postMutation.mutate(parsed.money.minorUnits, {
                      onSuccess: () => {
                        setPostingVariable(false);
                        setPostAmount('');
                      },
                      onError: (e) => {
                        setError(e instanceof ApiError ? e.message : 'Could not post');
                      },
                    });
                  }}
                >
                  Confirm
                </button>
              </div>
            ) : (
              <button
                type="button"
                className={ghostButtonInlineClass}
                disabled={postMutation.isPending}
                onClick={() => {
                  if (isVariable) {
                    setPostingVariable(true);
                    return;
                  }
                  setError(null);
                  postMutation.mutate(undefined, {
                    onError: (e) => {
                      setError(e instanceof ApiError ? e.message : 'Could not post');
                    },
                  });
                }}
              >
                Post now
              </button>
            )}
          </>
        )}
      </div>
      {error !== null && <p className="mt-1 text-sm text-danger">{error}</p>}
    </li>
  );
}

function UpcomingPanel(): React.JSX.Element {
  const { data: occurrences } = useUpcomingOccurrences(30);

  return (
    <div className="mb-6 rounded-[var(--radius-card)] border border-border bg-surface p-4">
      <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted">Coming up</p>
      {occurrences === undefined || occurrences.length === 0 ? (
        <p className="text-sm text-muted">Nothing in the next 30 days.</p>
      ) : (
        <ul className="space-y-2">
          {occurrences.map((o, i) => (
            <li
              key={`${o.templateId}-${String(i)}`}
              className="flex items-center justify-between text-sm"
            >
              <span className="text-ink">{o.name}</span>
              <span className="text-muted">{o.at.slice(0, 10)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

type TemplateKind = 'expense' | 'income' | 'transfer' | 'payable';
type MonthlyRuleKind = 'day' | 'firstWeekday' | 'lastDay';

function TemplateForm({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const { data: accounts } = useAccounts();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<TemplateKind>('expense');
  const [accountId, setAccountId] = useState('');
  const [toAccountId, setToAccountId] = useState('');
  const [party, setParty] = useState('');
  const [currency, setCurrency] = useState<CurrencyCode>('PKR');
  const [reason, setReason] = useState('');
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [behavior, setBehavior] = useState<'auto_post' | 'confirm'>('auto_post');
  const [frequency, setFrequency] = useState<RecurFrequency>('monthly');
  const [everyN, setEveryN] = useState('1');
  const [monthlyRuleKind, setMonthlyRuleKind] = useState<MonthlyRuleKind>('day');
  const [day, setDay] = useState('');
  const [weekday, setWeekday] = useState('1');
  const [startDate, setStartDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateRecurringTemplate();

  const amountCurrency: CurrencyCode =
    kind === 'payable'
      ? currency
      : ((accounts ?? []).find((a) => a.id === accountId)?.currency ?? 'PKR');
  const isVariable = amount.trim() === '';

  useEffect(() => {
    if (open) {
      setName('');
      setKind('expense');
      setAccountId('');
      setToAccountId('');
      setParty('');
      setCurrency('PKR');
      setReason('');
      setCategory('');
      setAmount('');
      setBehavior('auto_post');
      setFrequency('monthly');
      setEveryN('1');
      setMonthlyRuleKind('day');
      setDay('');
      setWeekday('1');
      setStartDate('');
      setError(null);
    }
  }, [open]);

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="New recurring template"
      submitLabel="Save"
      submitting={createMutation.isPending}
      onSubmit={() => {
        setError(null);
        if (name.trim() === '') {
          setError('Name is required');
          return;
        }
        if (startDate === '') {
          setError('Start date is required');
          return;
        }
        let amountMinor: number | undefined;
        if (!isVariable) {
          const parsed = parseAmountInput(amount, { defaultCurrency: amountCurrency });
          if (!parsed.ok) return; // AmountField already shows this inline
          amountMinor = parsed.money.minorUnits;
        }
        const effectiveBehavior = isVariable ? 'confirm' : behavior;

        let base: RecurringTemplateCreateRequest['base'];
        if (kind === 'expense' || kind === 'income') {
          if (accountId === '') {
            setError('Choose an account');
            return;
          }
          base = {
            kind,
            accountId,
            ...(amountMinor !== undefined ? { amountMinor } : {}),
            ...(category.trim() !== '' ? { category: category.trim() } : {}),
          };
        } else if (kind === 'transfer') {
          if (accountId === '' || toAccountId === '') {
            setError('Choose both accounts');
            return;
          }
          if (accountId === toAccountId) {
            setError('Choose two different accounts');
            return;
          }
          base = {
            kind: 'transfer',
            fromAccountId: accountId,
            toAccountId,
            ...(amountMinor !== undefined ? { amountMinor } : {}),
          };
        } else {
          if (party.trim() === '') {
            setError('Party is required');
            return;
          }
          if (reason.trim() === '') {
            setError('Reason is required');
            return;
          }
          base = {
            kind: 'payable',
            party: party.trim(),
            currency,
            reason: reason.trim(),
            ...(amountMinor !== undefined ? { amountMinor } : {}),
          };
        }

        const schedule: RecurringTemplateCreateRequest['schedule'] = {
          frequency,
          everyN: Math.max(1, Number(everyN) || 1),
          ...((frequency === 'monthly' || frequency === 'yearly') && monthlyRuleKind === 'day'
            ? {
                monthlyRule: {
                  kind: 'day' as const,
                  day: Math.max(1, Math.min(31, Number(day) || 1)),
                },
              }
            : {}),
          ...((frequency === 'monthly' || frequency === 'yearly') &&
          monthlyRuleKind === 'firstWeekday'
            ? { monthlyRule: { kind: 'firstWeekday' as const, weekday: Number(weekday) } }
            : {}),
          ...((frequency === 'monthly' || frequency === 'yearly') && monthlyRuleKind === 'lastDay'
            ? { monthlyRule: { kind: 'lastDay' as const } }
            : {}),
          ...(frequency === 'weekly' ? { weekday: Number(weekday) } : {}),
        };

        createMutation.mutate(
          {
            name: name.trim(),
            base,
            schedule,
            behavior: effectiveBehavior,
            startDate: new Date(startDate).toISOString(),
          },
          {
            onSuccess: () => {
              onOpenChange(false);
            },
            onError: (e) => {
              setError(e instanceof ApiError ? e.message : 'Could not save');
            },
          },
        );
      }}
    >
      <Field label="Name" htmlFor="recur-name">
        <input
          id="recur-name"
          className={inputClass}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
      </Field>
      <Field label="Kind" htmlFor="recur-kind">
        <select
          id="recur-kind"
          className={selectClass}
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as TemplateKind);
          }}
        >
          <option value="expense">Expense</option>
          <option value="income">Income</option>
          <option value="transfer">Transfer</option>
          <option value="payable">Payable</option>
        </select>
      </Field>

      {(kind === 'expense' || kind === 'income') && (
        <>
          <Field label="Account" htmlFor="recur-account">
            <AccountPicker id="recur-account" value={accountId} onChange={setAccountId} />
          </Field>
          <Field label="Category (optional)" htmlFor="recur-category">
            <input
              id="recur-category"
              className={inputClass}
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
              }}
            />
          </Field>
        </>
      )}
      {kind === 'transfer' && (
        <>
          <Field label="From account" htmlFor="recur-account">
            <AccountPicker id="recur-account" value={accountId} onChange={setAccountId} />
          </Field>
          <Field label="To account" htmlFor="recur-to-account">
            <AccountPicker id="recur-to-account" value={toAccountId} onChange={setToAccountId} />
          </Field>
        </>
      )}
      {kind === 'payable' && (
        <>
          <Field label="Party" htmlFor="recur-party">
            <input
              id="recur-party"
              className={inputClass}
              value={party}
              onChange={(e) => {
                setParty(e.target.value);
              }}
            />
          </Field>
          <Field label="Currency" htmlFor="recur-currency">
            <select
              id="recur-currency"
              className={selectClass}
              value={currency}
              onChange={(e) => {
                setCurrency(e.target.value as CurrencyCode);
              }}
            >
              {Object.keys(CURRENCIES).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Reason" htmlFor="recur-reason">
            <input
              id="recur-reason"
              className={inputClass}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </Field>
        </>
      )}

      <Field label="Amount (optional)" htmlFor="recur-amount">
        <AmountField
          id="recur-amount"
          currency={amountCurrency}
          value={amount}
          onChange={setAmount}
        />
      </Field>
      <Field label="Behavior" htmlFor="recur-behavior">
        <select
          id="recur-behavior"
          className={selectClass}
          value={isVariable ? 'confirm' : behavior}
          disabled={isVariable}
          onChange={(e) => {
            setBehavior(e.target.value as 'auto_post' | 'confirm');
          }}
        >
          <option value="auto_post">Auto-post</option>
          <option value="confirm">Confirm each time</option>
        </select>
      </Field>
      {isVariable && (
        <p className="mb-3 text-xs text-muted">
          Variable amounts must be confirmed each time (RC-2).
        </p>
      )}

      <Field label="Frequency" htmlFor="recur-frequency">
        <select
          id="recur-frequency"
          className={selectClass}
          value={frequency}
          onChange={(e) => {
            setFrequency(e.target.value as RecurFrequency);
          }}
        >
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
        </select>
      </Field>
      <Field label="Every N" htmlFor="recur-everyn">
        <input
          id="recur-everyn"
          className={inputClass}
          inputMode="numeric"
          value={everyN}
          onChange={(e) => {
            setEveryN(e.target.value);
          }}
        />
      </Field>
      {frequency === 'weekly' && (
        <Field label="Weekday" htmlFor="recur-weekday">
          <select
            id="recur-weekday"
            className={selectClass}
            value={weekday}
            onChange={(e) => {
              setWeekday(e.target.value);
            }}
          >
            {WEEKDAYS.map((d, i) => (
              <option key={d} value={i}>
                {d}
              </option>
            ))}
          </select>
        </Field>
      )}
      {(frequency === 'monthly' || frequency === 'yearly') && (
        <>
          <Field label="Rule" htmlFor="recur-rule">
            <select
              id="recur-rule"
              className={selectClass}
              value={monthlyRuleKind}
              onChange={(e) => {
                setMonthlyRuleKind(e.target.value as MonthlyRuleKind);
              }}
            >
              <option value="day">Day of month</option>
              <option value="firstWeekday">First weekday</option>
              <option value="lastDay">Last day</option>
            </select>
          </Field>
          {monthlyRuleKind === 'day' && (
            <Field label="Day of month" htmlFor="recur-day">
              <input
                id="recur-day"
                className={inputClass}
                inputMode="numeric"
                value={day}
                onChange={(e) => {
                  setDay(e.target.value);
                }}
              />
            </Field>
          )}
          {monthlyRuleKind === 'firstWeekday' && (
            <Field label="Weekday" htmlFor="recur-weekday-2">
              <select
                id="recur-weekday-2"
                className={selectClass}
                value={weekday}
                onChange={(e) => {
                  setWeekday(e.target.value);
                }}
              >
                {WEEKDAYS.map((d, i) => (
                  <option key={d} value={i}>
                    {d}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </>
      )}
      <Field label="Start date" htmlFor="recur-start">
        <input
          id="recur-start"
          type="date"
          className={inputClass}
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
          }}
        />
      </Field>

      {error !== null && <p className="text-sm text-danger">{error}</p>}
    </FormSheet>
  );
}

export function RecurringPage(): React.JSX.Element {
  const { data: templates, isLoading } = useRecurringTemplates();
  const { data: accounts } = useAccounts();
  const [formOpen, setFormOpen] = useState(false);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink">Recurring</h1>
        <button
          type="button"
          className="flex items-center gap-1 rounded-[var(--radius-control)] bg-primary px-3 py-1.5 text-sm font-medium text-primary-ink hover:opacity-90 active:opacity-80"
          onClick={() => {
            setFormOpen(true);
          }}
        >
          <Plus size={16} /> New template
        </button>
      </div>

      <UpcomingPanel />

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {!isLoading && (templates === undefined || templates.length === 0) && (
        <EmptyState
          icon={Repeat}
          message="No recurring templates yet."
          action={{
            label: 'New template',
            onClick: () => {
              setFormOpen(true);
            },
          }}
        />
      )}

      {templates !== undefined && templates.length > 0 && (
        <ul className="rounded-[var(--radius-card)] border border-border bg-surface px-4">
          {templates.map((t) => (
            <TemplateRow key={t.id} template={t} accounts={accounts ?? []} />
          ))}
        </ul>
      )}

      <TemplateForm open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
}
